import {
  convertData,
  DataSource,
  MutationOptions,
  QueryVariables,
  request,
  useExternalQuery,
  useLazyQuery,
  useMakeMutation,
} from '@shared/innmaxLib/services'
import {
  filter,
  flatten,
  isNil,
  mapAccum,
  path,
  pathOr,
  pipe,
  slice,
  uniqBy,
} from 'ramda'
import React from 'react'
import { useQuery, UseQueryOptions } from 'react-query'

import { DeviceType } from '@services/constants'
import {
  Device,
  IStatisticalByDeviceType,
  useConvertIDevice,
  useDeviceConverter,
  useLazySubDevice,
} from '@services/device'
import { ISession, useAllSessions } from '@services/session'
import {
  useChargingPointDetailConverter,
  useConvertChargingPoint,
} from './chargingpoint/chargingpoint.converter'

const { compose } = require('ramda')

export const useQueryMultipleDevice = () => {
  const convertDevice = useDeviceConverter()

  const fetchMultipleDevice = (devices = []) => {
    const tasks = pipe(
      filter((d: any) => d && !isNil(d.deviceType) && !isNil(d.id)),
      uniqBy((d: any) => d.id && d.deviceType)
    )(devices).map((d: any) => request(`/device/${d.deviceType}/${d.id}`))
    return Promise.all(tasks).then(result => {
      return result.map(r => convertDevice(path(['data'], r) as any))
    })
  }

  return {
    fetchMultipleDevice,
  }
}

export const useLazyQueryDevice = (options: QueryVariables = {}) => {
  const convertDevice = useDeviceConverter()

  const {
    isLoading: isLoadingSubDevice,
    isFetching: isFetchingSubDevice,
    refetch: fetchSubDevice,
    data: subDevice,
  } = useLazySubDevice(undefined)

  const { refetch, data, ...rest } = useLazyQuery('/device/{deviceType}/{id}', {
    ...options,
    select: res => path(['data'], res),
    keepPreviousData: true,
    onSuccess: (device: any) => {
      device &&
        device.subDeviceCount &&
        fetchSubDevice({
          parentDeviceSeq: device?.id,
          parentDeviceType: device?.deviceType,
        })
      return options.onSuccess && options.onSuccess(convertDevice(device))
    },
  })

  const device: Device | any = React.useMemo(() => (data ? data : null), [data]) //eslint-disable-line

  const subDevices: Device | unknown = React.useMemo(
    () => (data ? subDevice : null),
    [subDevice] //eslint-disable-line
  )

  const dataSource = React.useMemo(
    () =>
      data
        ? compose(convertDevice)({
            ...device,
            ...(device.subDeviceCount ? { subDevices } : null),
          })
        : null,
    [subDevices, device] //eslint-disable-line
  )

  return {
    ...rest,
    data: dataSource,
    isFetching: rest.isFetching || isFetchingSubDevice,
    loading: rest.isLoading || isLoadingSubDevice,
    loadingData: rest.isLoading || isLoadingSubDevice,
    queryDevice: refetch,
  }
}

export const useDeviceInfos = <T extends Device>({
  variables = {},
  ...options
}: QueryVariables = {}) => {
  const convertDevice = useDeviceConverter()

  const { data, refetch, ...others } = useLazyQuery(
    [
      // '/device/sampleDeviceInfos',
      '/device/chargingpoint/0/99999',
      { current: '', size: '', isSub: true, ...variables },
    ],
    {
      ...options,
      keepPreviousData: true,
    }
  )

  const dataSource: DataSource<T> = useConvertChargingPoint(data)

  // const dataSource: DataSource<T> = React.useMemo(
  //   () =>
  //     convertData<T>(
  //       {
  //         converter: convertDevice,
  //       },
  //       data
  //     ),
  //   [data] //eslint-disable-line
  // )

  const devicesBySessionId = React.useMemo(() => {
    const area: any[] = []

    dataSource?.content
      ?.filter((d: any) => d.isPositioned)
      .forEach(i => {
        if (i.sessionPath.length > 2) {
          slice(2, Infinity, i.sessionPath).forEach((s: any) => {
            if (!area[s.id as number]) {
              area[s.id as number] = []
            }
            area[s.id as number].push(i)
          })
        }

        const sencondSessionId = i?.sessionPath[1]?.id
        if (sencondSessionId) {
          if (!area[sencondSessionId as number]) {
            area[sencondSessionId as number] = []
          }
          area[sencondSessionId as number].push(i)
        }
      })

    return area
  }, [dataSource])

  const { byId } = useAllSessions()

  const sessionDeviceStatistical = React.useMemo(() => {
    const statisticalResult: Record<number, any[]> = {}
    pathOr([], ['data'], data).forEach((item: any) => {
      item?.connectors?.forEach((connector: any) => {
        let statistical = pathOr([], [item.sessionId], statisticalResult)

        let connectorStatistical: any = statistical.find(
          (s: any) => s.deviceType === DeviceType.CONNECTOR
        )
        if (!connectorStatistical) {
          connectorStatistical = {
            summaryAvailableCount: 0,
            summaryChargingCount: 0,
            summaryUnavailableCount: 0,
            deviceType: DeviceType.CONNECTOR,
            sessionId: item.sessionId,
            connectorCount: 0,
          }
          statisticalResult[item.sessionId] = [connectorStatistical]
        }
        const { detailedStatus } = connector
        connectorStatistical.connectorCount++

        if (detailedStatus === 'Available') {
          connectorStatistical.summaryAvailableCount++
        }
        if (
          [
            'Preparing',
            'Charging',
            'SuspendedEVSE',
            'SuspendedEV',
            'Finishing',
            'Reserved',
          ].indexOf(detailedStatus) > -1
        ) {
          connectorStatistical.summaryChargingCount++
        }
        if (['Unavailable', 'Faulted'].indexOf(detailedStatus) > -1) {
          connectorStatistical.summaryUnavailableCount++
        }
        const recrusiveForParent = (session: any) => {
          if (!byId[session.parentSessionId]) {
            return
          }
          statisticalResult[session.parentSessionId] = [connectorStatistical]
          return recrusiveForParent(byId[session.parentSessionId])
        }
        // 如果有父層session，則將統計結果也放到父層
        recrusiveForParent(byId[item.sessionId])
      })
    })
    return statisticalResult
  }, [data])

  return {
    ...others,
    refetch,
    loading: others.isFetching,
    queryDeviceInfos: refetch,
    dataSource: dataSource?.content || [],
    devicesBySessionId,
    sessionDeviceStatistical,
  }
}

/**
 *  設備清單
 * @param deep:是否搜尋區域深層
 */
export const useDevices = <T extends Device>(
  query?: {
    [key: string]: any
  },
  options?: UseQueryOptions
) => {
  const convertDevice = useDeviceConverter()

  const { data, ...rest } = useQuery(
    ['/device/{deviceType}/{pageNum}/{pageSize}', { deep: true, ...query }],
    { cacheTime: 0, ...options }
  )

  const dataSource: DataSource<T> = React.useMemo(
    () =>
      convertData<T>(
        {
          converter: convertDevice,
        },
        data
      ),
    [data] //eslint-disable-line
  )

  return {
    ...rest,
    dataSource,
    data,
    loading: rest.isLoading,
  }
}

export const useDevice = (
  {
    deviceType,
    id,
  }: {
    deviceType: string
    id?: React.Key
  },
  options?: UseQueryOptions
) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    [`/device/{deviceType}/{id}`, { deviceType, id }],
    {
      enabled: !!id,
      cacheTime: 0,
      select: res => path(['data'], res),
      ...options,
    }
  )

  const chargingPointDetailconverter = useChargingPointDetailConverter()

  const { data: deviceData } = useConvertIDevice(data)

  if (deviceType === DeviceType.CHARGINGPOINT) {
    return {
      ...others,
      refetch,
      loading: isLoading,
      data: chargingPointDetailconverter(data),
    }
  }

  return {
    ...others,
    refetch,
    loading: isLoading,
    data: deviceData,
  }
}

export const createDeviceUrl = '/device/{deviceType}'

export const createDevice = (values: Partial<any>) =>
  request(createDeviceUrl, { method: 'POST', body: values })

export const useAddDevice = (options: MutationOptions = {}) => {
  return useMakeMutation(createDevice, options)
}

export const deleteDevice = (values: { deviceType: string; id: React.Key }) =>
  request(`/device/{deviceType}/{id}`, { method: 'DELETE', body: values })

export const updateDeviceUrl = '/device/{deviceType}/{id}'

export const updateDevice = (values: Partial<any> = {}) =>
  request(updateDeviceUrl, {
    method: 'PUT',
    body: values,
  })

export const useUpdateDevice = (options: MutationOptions = {}) => {
  return useMakeMutation(updateDevice, options)
}

export const useDeleteDevice = (options: MutationOptions = {}) => {
  return useMakeMutation(deleteDevice, options)
}

export const useDeviceExport = (query?: any, options?: QueryVariables) => {
  const [enabled, setIsEnabled] = React.useState(false)

  const { isLoading } = useQuery(
    ['/device/{deviceType}/export', { ...query }],
    {
      ...options,
      enabled,
      onSuccess: (response: any) => {
        if (options?.onSuccess) {
          options.onSuccess(response)
        }
        setIsEnabled(false)
      },
      onError: error => {
        console.log(error)
        setIsEnabled(false)
      },
    }
  )

  return [
    isLoading,
    () => {
      setIsEnabled(true)
    },
  ] as [boolean, () => void]
}

export const useSLGESCRealtimeStatus = () => {
  return useExternalQuery({
    key: 'RealtimeStatus',
    url: 'https://www.slgesc.nat.gov.tw/RealtimeStatus',
  })
}

export const useSLGESCPowerMonthSummary = ({ year, month }: any) => {
  return useExternalQuery({
    key: 'PowerMonthSummary',
    url: `https://www.slgesc.nat.gov.tw/PowerMonthSummary/${year}/${month}`,
  })
}

export const useSLGESCWeatherReport = () => {
  return useExternalQuery({
    key: 'WeatherReport',
    url: 'https://www.slgesc.nat.gov.tw/WeatherReport',
  })
}

// for testing
// export const usePublicDevice = ({ deviceType, deviceId }: any) => {
//   return useExternalQuery({
//     key: 'PublicDevice',
//     url: `https://testing2.f2fcloud.com/SCTainanShalun_3_0/api/v1/public/device/${deviceType}/byDeviceId/${deviceId}`,
//   })
// }

export const usePublicDevice = (
  query?: {
    [key: string]: any
  },
  options?: UseQueryOptions
): any => {
  const { data, isLoading, ...rest } = useQuery(
    [
      '/public/device/{deviceType}/byDeviceId/{deviceId}',
      { deep: true, ...query },
    ],
    { cacheTime: 0, ...options }
  )

  return {
    ...rest,
    data,
    isLoading,
  }
}

export const useSessionDeviceStatistical = (
  query: Record<string, any> = {}
) => {
  const { data, isLoading, isFetching, isPreviousData, ...others } =
    useLazyQuery(
      ['/device/report/seesionStatisticalByDeviceType', { ...query }],
      {
        cacheTime: Infinity,
        keepPreviousData: true,
      }
    )

  const recrusiveForChildren = (session: any) => {
    if (session.children.length === 0) {
      return [session.id]
    }
    return session.children.map((child: any) => {
      return recrusiveForChildren(child)
    })
  }

  const [_data, deviceCountInfoBySessionId] = React.useMemo(() => {
    const _data = pathOr([], ['data'], data) as IStatisticalByDeviceType[]

    const [result] = mapAccum<
      IStatisticalByDeviceType,
      Record<string, IStatisticalByDeviceType[]>,
      IStatisticalByDeviceType
    >(
      (acc, v) => {
        if (!acc[v.sessionId]) {
          acc[v.sessionId] = []
        }
        acc[v.sessionId].push(v)
        return [acc, v]
      },
      {},
      _data || []
    )
    return [_data, result]
  }, [data])

  /**
   * 取得圖層深度統計
   * @param session
   * @param statisticalSource 分析資料來源，若無則使用 API 取得的資料
   */
  const getSessionDeepStatistical = React.useCallback(
    (
      session?: ISession | null,
      statisticalSource?: Record<string, IStatisticalByDeviceType[]> | null
    ) => {
      if (!session) {
        return []
      }
      const allSessionIds = flatten(recrusiveForChildren(session))
      const sessionDeepStatisticalList = flatten(
        allSessionIds.map((id: number) =>
          path(
            [id],
            statisticalSource ? statisticalSource : deviceCountInfoBySessionId
          )
        )
      ) as IStatisticalByDeviceType[]
      return sessionDeepStatisticalList
    },
    [deviceCountInfoBySessionId]
  )

  return {
    ...others,
    isLoading: isFetching,
    deviceCountInfoBySessionId,
    data: _data,
    getSessionDeepStatistical,
  }
}
